import React, { useState } from 'react';
import { api, extractApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { MessageCircleQuestion, CheckCircle2 } from 'lucide-react';

/**
 * FeedbackDialog — reusable modal for capturing user feedback.
 *
 * Anyone (signed-in or anon) can submit. When signed in the backend
 * snapshots the user's identity so admins can reply. Anon submissions
 * carry an optional name+email so admin can still respond.
 *
 * Props:
 *   trigger  — optional custom trigger element. If omitted a default
 *              "Send Feedback" button is rendered.
 *   source   — analytics context (e.g. "footer", "post-inquiry", "dashboard").
 */
export const FeedbackDialog = ({ trigger, source = 'unknown' }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    category: 'suggestion',
    subject: '',
    message: '',
    name: '',
    email: '',
  });
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setForm({ category: 'suggestion', subject: '', message: '', name: '', email: '' });
    setSubmitted(false);
  };

  const submit = async () => {
    if (!form.subject.trim() || form.subject.trim().length < 3) {
      toast.error('Subject is too short'); return;
    }
    if (!form.message.trim() || form.message.trim().length < 10) {
      toast.error('Please add a bit more detail (min 10 characters)'); return;
    }
    setBusy(true);
    try {
      const payload = {
        category: form.category,
        subject: form.subject.trim(),
        message: form.message.trim(),
        page_url: (typeof window !== 'undefined' ? window.location.pathname : '') + `?src=${source}`,
      };
      if (!user) {
        if (form.name.trim()) payload.name = form.name.trim();
        if (form.email.trim()) payload.email = form.email.trim();
      }
      await api.post('/feedback', payload);
      setSubmitted(true);
    } catch (e) {
      toast.error(extractApiError(e, 'Failed to send feedback'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant='outline' size='sm' data-testid='feedback-trigger'>
            <MessageCircleQuestion className='h-4 w-4 mr-1.5' /> Send feedback
          </Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid='feedback-dialog' className='max-w-lg max-h-[90vh] overflow-y-auto'>
        {submitted ? (
          <div className='py-6 text-center space-y-3'>
            <CheckCircle2 className='h-12 w-12 text-emerald-600 mx-auto' />
            <div className='text-lg font-semibold'>Thanks for the feedback!</div>
            <div className='text-sm text-muted-foreground'>
              We read every submission and reply when we can. You&apos;ll hear back on your registered email if we need more info.
            </div>
            <Button onClick={() => setOpen(false)} data-testid='feedback-close-btn'>Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Send feedback</DialogTitle>
              <DialogDescription>
                Suggest an improvement, request a feature, report a bug, or share anything else — we&apos;re actively building.
              </DialogDescription>
            </DialogHeader>
            <div className='space-y-3'>
              <div>
                <Label className='text-xs'>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid='feedback-category'><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value='suggestion'>Suggestion</SelectItem>
                    <SelectItem value='feature_request'>Feature Request</SelectItem>
                    <SelectItem value='bug'>Bug</SelectItem>
                    <SelectItem value='other'>Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className='text-xs'>Subject</Label>
                <Input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  maxLength={140}
                  placeholder='One-line summary'
                  data-testid='feedback-subject'
                />
              </div>
              <div>
                <Label className='text-xs'>Details</Label>
                <Textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  maxLength={4000}
                  placeholder='What happened, and what you expected to happen'
                  data-testid='feedback-message'
                />
              </div>
              {!user && (
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-md border bg-secondary/30 p-2'>
                  <div>
                    <Label className='text-xs'>Your name (optional)</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={120} data-testid='feedback-anon-name' />
                  </div>
                  <div>
                    <Label className='text-xs'>Email (so we can reply)</Label>
                    <Input type='email' value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid='feedback-anon-email' />
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
              <Button onClick={submit} disabled={busy} data-testid='feedback-submit'>
                {busy ? 'Sending…' : 'Send feedback'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
